import { useToast } from "@codat/orchard-ui";
import {
  AlertingRule,
  GetCompaniesResponse,
  useEditRuleMutation,
} from "api/public";
import React from "react";
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  useForm,
} from "react-hook-form";
import { clientRuleTypes } from "utils/constants";

import Button from "components/Button";
import Modal from "components/Modal";
import ModalBody from "components/ModalBody";
import SelectInput from "components/SelectInput";
import TextArea from "components/TextArea";
import TextInput from "components/TextInput";

import { formatEmails, validateEmails, validateWebhookUrl } from "../utils";
import styles from "../WebhookRules.module.css";
import { formatRuleName } from "../WebhookRulesTable/utils";

interface EditRuleModalProps {
  rule: AlertingRule;
  onClose: () => void;
  onSuccess: () => void;
  companies: GetCompaniesResponse | undefined;
}

interface FormData {
  company: { value?: string };
  emails: string;
  webhookUrl?: string;
}

const EditRuleModal: React.FC<EditRuleModalProps> = ({
  onSuccess,
  rule,
  companies,
  onClose,
}) => {
  const { addToast } = useToast();
  const formattedRuleType = formatRuleName(rule.type);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    mode: "onBlur",
    defaultValues: {
      company: { value: rule.companyId },
      emails: rule.notifiers.emails.join(",\n"),
      webhookUrl: rule.notifiers.webhook,
    },
  });

  const [{ loading }, editRule] = useEditRuleMutation({
    ruleId: rule.id,
  });

  const onSubmit = async (formData: FormData) => {
    const { company, emails, webhookUrl } = formData;
    const data = {
      id: rule.id,
      type: rule.type,
      companyId: company?.value,
      notifiers: {
        emails: formatEmails(emails),
        webhook: webhookUrl ? webhookUrl : undefined,
      },
    };
    try {
      await editRule({ data });
      addToast({
        variant: "success",
        message: "Rule updated",
      });
      onSuccess();
    } catch (error) {
      addToast({
        variant: "error",
        message: "Failed to update rule",
      });
    }
  };
  const companySelect = (
    field: ControllerRenderProps<FormData, "company">,
    fieldState: ControllerFieldState
  ): React.ReactElement => (
    <SelectInput
      label="Company"
      showLabel
      inputName="Company"
      id="company"
      data-testid="company-input"
      isSkeleton={!companies}
      placeholder="Select..."
      errorMessage={fieldState?.error?.message}
      error={!!fieldState?.error?.message}
      items={
        companies?.results
          ? [
              { value: "", label: "All companies" },
              ...companies.results.map((c) => ({
                value: c.id,
                label: c.name,
              })),
            ]
          : []
      }
      handleSelectedItemChange={field.onChange}
      ref={field.ref}
      initialSelectedItem={
        rule.companyId && companies?.results
          ? {
              value: rule.companyId,
              label:
                companies.results.find((c) => c.id === rule.companyId)?.name ||
                "Company not found",
            }
          : { value: "All", label: "All companies" }
      }
      data-autofocus
    />
  );

  return (
    <Modal onClose={onClose} isPinned>
      {rule && (
        <ModalBody
          size="medium"
          overline="Manage rule"
          title={formattedRuleType}
          subtitle={rule.id}
          className={styles.formModal}
          data-testid="edit-rule-modal"
          onClose={onClose}
        >
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <Controller
              name="company"
              control={control}
              render={({ field, fieldState }) =>
                clientRuleTypes.includes(rule.type) ? (
                  <></>
                ) : (
                  companySelect(field, fieldState)
                )
              }
            />
            <TextArea
              className={styles.textarea}
              label="Email addresses to notify"
              id="emails-to-notify"
              inputName="Notification Emails"
              description="A comma-separated list of emails to notify when an alert for this rule is triggered"
              error={!!errors?.emails}
              errorMessage={errors.emails?.message}
              {...register("emails", {
                validate: validateEmails,
              })}
            />
            <TextInput
              label="Webhook notification URL"
              inputName="Webhook notification URL"
              id="webhook-notification-url"
              placeholder="https://www.website.com"
              error={!!errors.webhookUrl}
              errorMessage={errors.webhookUrl?.message}
              {...register("webhookUrl", {
                validate: (val) => validateWebhookUrl(val || ""),
              })}
            />
            <div className={styles.footerContainer}>
              <Button
                data-testid="cancel-button"
                className={styles.cancelButton}
                label="Cancel"
                buttonName="Cancel"
                variant="secondary"
                color="interface"
                onClick={onClose}
              />
              <Button
                buttonName="Save"
                label="Save changes"
                isLoading={loading}
              />
            </div>
          </form>
        </ModalBody>
      )}
    </Modal>
  );
};

export default EditRuleModal;
